/**
 * Shared cover-page content model for exports.
 *
 * Encodes the cover page's data ONCE so the PDF (React) and Word (docx) render
 * the same fields the same way — matching SerVet_Export_Reference_*.{pdf,docx}.
 */

import type { ExportCompanyIdentity } from './companyIdentity';
import { TO_BE_COMPLETED } from './companyIdentity';
import { decodeHtmlEntities, cleanTitle } from './textCleanup';

const HEX32 = /^[0-9a-f]{32}$/i;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Resolve the HUMAN solicitation number (e.g. 36C10X26Q0124), preferring the
 * cover letter's subject. Avoids SAM's internal notice_id/UUID (e.g.
 * 627e7cc1490a4ed38003fa5f997cb6cc), which is not what a CO recognizes.
 */
export function resolveSolicitationNumber(contract: any, coverLetter?: any): string {
  const candidates = [
    coverLetter?.subject?.solicitationNumber,
    contract?.solicitationNumber,
    contract?.notice_id,
    contract?.id,
  ].map((v) => (v == null ? '' : decodeHtmlEntities(String(v)).trim()));
  // Prefer a human-readable value (not a 32-hex / UUID).
  for (const v of candidates) {
    if (v && !HEX32.test(v) && !UUID.test(v)) return v;
  }
  // Last resort: first non-empty even if it's an internal id.
  return candidates.find(Boolean) || '';
}

export interface CoverMetaRow {
  label: string;
  value: string;
}

export interface CoverPageModel {
  wordmark: string;            // company name (cover wordmark)
  tagline: string;             // optional short tagline ('' if none)
  eyebrow: string;             // e.g. "REQUEST FOR QUOTE — OFFEROR RESPONSE"
  title: string;               // proposal/solicitation title
  subtitle: string;            // issuing office / agency
  volumeLabel: string;         // e.g. "Cover Letter" / "Complete Proposal" ('' to hide)
  meta: CoverMetaRow[];        // Solicitation No, Title, NAICS, Set-Aside, Date
  submittedTo: string[];       // CO/CS block lines
  submittedBy: string[];       // offeror block lines
  date: string;                // formatted submission date
  confidentialityNote: string;
  identity: ExportCompanyIdentity;
}

function clean(v: unknown): string {
  if (v == null) return '';
  return decodeHtmlEntities(String(v)).trim();
}

export function formatLongDate(d?: Date | string): string {
  let date: Date;
  if (d instanceof Date) date = d;
  else if (typeof d === 'string' && d) date = new Date(d);
  else date = new Date();
  if (isNaN(date.getTime())) return clean(d);
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
}

function deriveEyebrow(type?: string): string {
  const t = (type || '').toLowerCase();
  if (/quote|rfq/.test(t)) return 'REQUEST FOR QUOTE — OFFEROR RESPONSE';
  if (/proposal|rfp/.test(t)) return 'REQUEST FOR PROPOSAL — OFFEROR RESPONSE';
  if (/quotation/.test(t)) return 'REQUEST FOR QUOTATION — OFFEROR RESPONSE';
  return 'SOLICITATION — OFFEROR RESPONSE';
}

function joinNonEmpty(parts: Array<string | undefined | null>, sep = ', '): string {
  return parts.map(clean).filter(Boolean).join(sep);
}

export interface BuildCoverOptions {
  volumeLabel?: string;
  tagline?: string;
  submissionDate?: Date | string;
  /** Parsed cover-letter object — supplies solicitation no. / NAICS the contract row may lack. */
  coverLetter?: any;
}

export function buildCoverPageModel(
  contract: any,
  identity: ExportCompanyIdentity,
  opts: BuildCoverOptions = {}
): CoverPageModel {
  const c = contract || {};
  const cl = opts.coverLetter || {};
  const solicitation = resolveSolicitationNumber(c, cl);
  const title = cleanTitle(c.title) || cleanTitle(cl?.subject?.contractTitle) || 'Proposal Response';
  const date = formatLongDate(opts.submissionDate);

  // ----- Identity / meta block -----
  const meta: CoverMetaRow[] = [];
  if (solicitation) meta.push({ label: 'Solicitation No.', value: solicitation });
  if (title) meta.push({ label: 'Title', value: title });
  const naics = clean(c.naicsCode) || clean(c.naics_code)
    || (Array.isArray(c.naicsCodes) ? clean(c.naicsCodes[0]) : '')
    || (Array.isArray(c.naics_codes) ? clean(c.naics_codes[0]) : '')
    || clean(cl?.subject?.naicsCode);
  if (naics) meta.push({ label: 'NAICS', value: naics });
  const setAsideArr = Array.isArray(c.setAsides) ? c.setAsides
    : Array.isArray(c.set_asides) ? c.set_asides : null;
  const setAside = setAsideArr ? joinNonEmpty(setAsideArr) : (clean(c.setAside) || clean(c.set_aside));
  if (setAside) meta.push({ label: 'Set-Aside', value: setAside });
  meta.push({ label: 'Date', value: date });

  // ----- Submitted To (government) -----
  // Prefer the cover letter's recipient block (reliable, normalized JSON). The
  // runtime contract may be a snake_case sam_contracts row, so accept both cases.
  const r = cl?.recipient || {};
  const co = c.contractingOfficer || c.contracting_officer || {};
  const poc = c.pointOfContact?.primary || c.point_of_contact?.primary || {};
  const office = clean(r.office) || clean(c.subTier) || clean(c.sub_tier)
    || clean(c.department) || clean(c.organizationType);
  const oa = c.officeAddress || c.office_address || {};
  const addrLines = clean(r.address)
    ? String(r.address).split('\n').map(clean).filter(Boolean)
    : [
        clean(oa.street1 || oa.street),
        joinNonEmpty([oa.city, joinNonEmpty([oa.state || oa.stateCode, oa.zip], ' ')]),
      ].filter(Boolean);

  const submittedTo = [
    joinNonEmpty([clean(r.name) || clean(co.name) || clean(poc.name), clean(r.title) || clean(co.title) || clean(poc.title)]),
    office,
    clean(r.agency) || clean(c.agency),
    ...addrLines,
  ].filter(Boolean);

  // ----- Submitted By (offeror) -----
  const submittedBy = [
    identity.companyName,
    ...identity.addressLines,
    identity.pocName
      ? joinNonEmpty([`POC: ${identity.pocName}`, identity.phone], '  ·  ')
      : identity.phone,
    `UEI: ${identity.uei || TO_BE_COMPLETED}  ·  CAGE: ${identity.cage || TO_BE_COMPLETED}`,
  ].filter(Boolean);

  return {
    wordmark: identity.companyName,
    tagline: clean(opts.tagline),
    eyebrow: deriveEyebrow(c.type),
    title,
    subtitle: office || clean(c.agency),
    volumeLabel: clean(opts.volumeLabel),
    meta,
    submittedTo,
    submittedBy,
    date,
    confidentialityNote:
      'This document contains confidential and proprietary information submitted in ' +
      'response to the above solicitation. It shall not be disclosed outside the ' +
      'Government and shall not be duplicated, used, or disclosed in whole or in part ' +
      'for any purpose other than to evaluate this submission.',
    identity,
  };
}
